import type { RouteObject } from 'react-router-dom';

import KnowYourLoan from '~/common/components/KnowYourLoan/KnowYourLoan';
import BorrowerSummary from '~/common/components/KnowYourLoan/components/BorrowerSummary';
import SummaryWrapper from '~/common/components/Nav/SummaryWrapper/SummaryWrapper';
import {
  AssetAuditHistory,
  AssetBankStatementAuditHistory,
  AssetReconciliationAuditHistory,
  AuditHistory,
  AuditHistoryDynamicTabs,
  DepositSourcingAuditHistory,
  IncomeAuditHistory,
  IncomeCalculatorAuditHistory,
  IncomeCalculatorAuditHistoryV2,
  MortgageHistoryAuditHistory,
  QueueManagerAuditHistory,
  SectionedAuditHistory,
  TransferReconciliationAuditHistory,
  ValidateDocumentAuditHistory,
} from '~/pages/AuditHistory';
import AuditHistoryWrapper from '~/pages/AuditHistory/Wrapper';
import Home from '~/pages/Home/Home';
import IncomeWorksheet from '~/pages/IncomeWorksheet/IncomeWorksheet';
import { Assets1003 } from '~/pages/Loan1003DataGrids/Assets/Assets1003';
import { Borrower1003 } from '~/pages/Loan1003DataGrids/Borrower/Borrower1003';
import Income1003Layout from '~/pages/Loan1003DataGrids/Income';
import { Liabilities1003 } from '~/pages/Loan1003DataGrids/Liabilities/Liabilities1003';
import RealEstateOwned1003Layout from '~/pages/Loan1003DataGrids/RealEstateOwned/RealEstateOwned';
import SubjectProperty1003Layout from '~/pages/Loan1003DataGrids/SubjectProperty/SubjectProperty';
import NotFound from '~/pages/NotFound';

import AUSHistory from './modals/AUS/AUSHistory';

type RouteMetadata = {
  isUnderwriterOnly?: boolean;
  isAuditHistoryRouteEnabled?: boolean;
};

const routes: (RouteObject & RouteMetadata)[] = [
  {
    path: '/',
    element: <Home />,
  },
  {
    path: '/:lenderDatabaseId/:loanRecordId/:slug?',
    element: <Home />,
  },
  {
    path: '/:lenderDatabaseId/:loanRecordId/summary',
    element: <SummaryWrapper />,
    children: [
      {
        path: 'borrower/readOnly?',
        element: <Borrower1003 />,
      },
      {
        path: 'liabilities/readOnly?',
        element: <Liabilities1003 />,
      },
      {
        path: 'income/readOnly?',
        element: <Income1003Layout />,
      },
      {
        path: 'subject-property/readOnly?',
        element: <SubjectProperty1003Layout />,
      },
      {
        path: 'assets/readOnly?',
        element: <Assets1003 />,
      },
      {
        path: 'reo/readOnly?',
        element: <RealEstateOwned1003Layout />,
      },
    ],
  },
  {
    path: '/:lenderDatabaseId/:loanRecordId/aus/history',
    element: <AUSHistory />,
  },
  {
    path: '/:lenderDatabaseId/:loanRecordId/income-worksheet',
    element: <IncomeWorksheet />,
  },
  {
    path: '/:lenderDatabaseId/:loanRecordId/know-your-loan',
    element: <KnowYourLoan />,
    isUnderwriterOnly: true,
    children: [
      {
        path: '/:lenderDatabaseId/:loanRecordId/know-your-loan/:category',
        element: <BorrowerSummary />,
      },
    ],
  },
  {
    path: '/:lenderDatabaseId/:loanRecordId/audit-history',
    element: <AuditHistoryWrapper />,
    isUnderwriterOnly: true,
    isAuditHistoryRouteEnabled: true,
    children: [
      {
        path: ':pageEnum',
        element: <AuditHistory />,
      },
      {
        path: 'summary/:category/:pageEnum',
        element: <AuditHistoryDynamicTabs page="summary-data" />,
      },
      {
        path: 'summary/borrower/:pageEnum',
        element: <AuditHistoryDynamicTabs page="borrower1003" />,
      },
      {
        path: 'status-and-review',
        element: <AuditHistoryDynamicTabs page="status-and-review" />,
      },
      {
        path: ':pageEnum/:id/:entityEnum',
        element: <AuditHistory />,
      },
      {
        path: 'assets',
        children: [
          {
            path: ':assetId/:assetTypeId',
            element: <AssetAuditHistory />,
          },
          {
            path: ':assetId/:assetTypeId/:pageEnum',
            element: <DepositSourcingAuditHistory />,
          },
          {
            path: 'bankStatement/:assetId/:documentId',
            element: <AssetBankStatementAuditHistory />,
          },
        ],
      },
      {
        path: 'income',
        children: [
          {
            path: 'validateDocument/:customerRecordId/:employerId/:documentId',
            element: <ValidateDocumentAuditHistory />,
          },
          {
            path: ':entity/:boltAuditPage/:applicableIncomeAuditId/:applicableIncomeAuditType/:employerId/:customerRecordId',
            element: <IncomeAuditHistory />,
          },
          {
            path: 'calculate/:customerRecordId/:employerId/:calculatorTypeId',
            element: <IncomeCalculatorAuditHistory />,
          },
          {
            path: 'calculate/:calculatorType',
            element: <IncomeCalculatorAuditHistoryV2 />,
          },
        ],
      },
      {
        path: 'transfer-reconciliation/:pageEnum',
        element: <TransferReconciliationAuditHistory />,
      },
      {
        path: 'asset-reconciliation/:pageEnum',
        element: <AssetReconciliationAuditHistory />,
      },
      {
        path: 'queue-manager',
        element: <QueueManagerAuditHistory />,
      },
      {
        path: 'sectioned/:pageEnum/:entityEnum?',
        element: <SectionedAuditHistory />,
      },
      {
        path: 'mortgage-history/:pageEnum/:entityEnum',
        element: <MortgageHistoryAuditHistory />,
      },
      {
        path: 'summary',
        element: <NotFound />,
      },
      {
        path: 'sectioned',
        element: <NotFound />,
      },
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
